import * as React from "react";

import { OLD_NOTES_MAX, type RemovedRoomNote } from "@/lib/spec-base-changes";

import { OldBesideNew, StruckText } from "./old-beside-new";

// The changed-from-base highlight's room list — #1043's variant E for a
// specific line whose rooms changed (ledger §7, "Changed from base widens"):
// every current room in its order, the ones new to the base as `+ name` in
// `--color-spec-room-added`, then the base's rooms this line no longer has,
// struck in the pair's grey. Comma-separated, as the plain room text it
// replaces (`getLocationNames`). Nothing hangs on the colour alone: an added
// room carries its `+` and is announced "Room added:", a removed one is struck
// and announced "Room removed:".

export interface RoomChangeListProps {
  /** Every current room, in order; `added` when the base line has no such room. */
  rooms: readonly { name: string; added: boolean }[];
  /** The base line's rooms this line no longer has, in their order. */
  removed: readonly string[];
}

export function RoomChangeList({ rooms, removed }: RoomChangeListProps) {
  const entries: React.ReactNode[] = [
    ...rooms.map((room) =>
      room.added ? (
        <span data-room="added" className="text-spec-room-added">
          <span className="sr-only">Room added: </span>
          <span aria-hidden="true">+ </span>
          {room.name}
        </span>
      ) : (
        <span data-room="kept">{room.name}</span>
      ),
    ),
    ...removed.map((name) => (
      <del data-room="removed" className="text-mui-grey-700 decoration-mui-grey-700 line-through">
        <span className="sr-only">Room removed: </span>
        {name}
      </del>
    )),
  ];
  return (
    <span data-testid="room-change-list">
      {entries.map((entry, index) => (
        // A fixed list per render whose names may repeat: the index is the identity.
        <React.Fragment key={index}>
          {index > 0 ? ", " : null}
          {entry}
        </React.Fragment>
      ))}
    </span>
  );
}

/**
 * A line's removed rooms that had notes (#1106 D8), in a notes cell: each
 * room's name struck as the list strikes it, announced "Room removed:", over
 * its old notes' text struck above *None* (`OldBesideNew`'s block, cut at 140).
 */
export function RemovedRoomNotes({ rooms }: { rooms: readonly RemovedRoomNote[] }) {
  return rooms.map((room, index) => (
    // The base's rooms, whose names may repeat: the index is the identity.
    <div key={index} data-room-note="removed" className="flex flex-col pb-2 text-left">
      <StruckText className="text-[0.875rem] font-medium">
        <span className="sr-only">Room removed: </span>
        {room.name}
      </StruckText>
      <OldBesideNew old={room.notes} cutAt={OLD_NOTES_MAX} layout="block">
        {null}
      </OldBesideNew>
    </div>
  ));
}
